/**
 * PlayLastCallButton — small round Play icon that fetches the LAST call
 * with a recording for a candidate and streams it inline (no navigation).
 *
 * Behaviour:
 *   - Silent if the candidate has no call yet, or no recording is ready.
 *   - Clicking spawns/toggles a floating inline mini-player (audio element)
 *     that streams from `/api/twilio/calls/{id}/recording` (auth-gated).
 *   - Second click closes the player.
 *
 * We intentionally keep this component completely self-contained (no
 * global player state) so it can be dropped anywhere.
 */
import { Loader2, Pause, Play } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import { api, API_BASE, tokenStore } from "../../lib/api";

export default function PlayLastCallButton({ candidate, dataTestid, className = "" }) {
    const [lastCall, setLastCall] = useState(null); // { id, recording_url, ... }
    const [audioSrc, setAudioSrc] = useState(null);
    const [loading, setLoading] = useState(false);
    const [playing, setPlaying] = useState(false);
    const audioRef = useRef(null);
    const objectUrlRef = useRef(null);

    // Look for a call with a recording (peek at only the first record).
    // iter92 · Skip the API entirely when we already know there is no call
    // to fetch — a candidate with call_attempts=0 cannot have a recording.
    // Prevents the 500-cards × 2-effects storm that saturated the ingress
    // rate limiter on /candidats and silently 429'd unrelated user actions
    // (layan/offers dropdown, manual-from-call POST, etc.).
    useEffect(() => {
        if (!candidate?.id) return;
        const attempts = Number(candidate?.call_attempts || 0);
        if (attempts <= 0) {
            setLastCall(null);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get("/twilio/calls", {
                    params: { candidate_id: candidate.id },
                });
                if (cancelled) return;
                const first = Array.isArray(data)
                    ? data.find((c) => c.recording_url)
                    : null;
                setLastCall(first || null);
            } catch (_e) {
                // silent — the button just hides itself
            }
        })();
        return () => { cancelled = true; };
    }, [candidate?.id, candidate?.call_attempts]);

    // Cleanup blob URL on unmount
    useEffect(() => () => {
        if (objectUrlRef.current) {
            URL.revokeObjectURL(objectUrlRef.current);
            objectUrlRef.current = null;
        }
    }, []);

    const load = useCallback(async () => {
        if (!lastCall) return null;
        setLoading(true);
        try {
            const token = tokenStore.get();
            const res = await fetch(`${API_BASE}/twilio/calls/${lastCall.id}/recording`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            objectUrlRef.current = url;
            setAudioSrc(url);
            return url;
        } catch (_e) {
            return null;
        } finally {
            setLoading(false);
        }
    }, [lastCall]);

    const toggle = useCallback(async (e) => {
        e?.preventDefault?.();
        e?.stopPropagation?.();
        if (!lastCall) return;
        // Lazy-load the blob on first click
        if (!audioSrc) {
            const url = await load();
            if (!url) return;
            // Play once loaded
            setTimeout(() => {
                try {
                    audioRef.current?.play();
                    setPlaying(true);
                } catch (_e) { /* ignore */ }
            }, 60);
            return;
        }
        if (playing) {
            audioRef.current?.pause();
            setPlaying(false);
        } else {
            try {
                audioRef.current?.play();
                setPlaying(true);
            } catch (_e) { /* ignore */ }
        }
    }, [audioSrc, lastCall, load, playing]);

    if (!lastCall) return null;

    return (
        <>
            <button
                type="button"
                onClick={toggle}
                data-testid={dataTestid}
                title={playing ? "Mettre en pause" : "Écouter le dernier appel enregistré"}
                className={`h-9 w-9 inline-flex items-center justify-center rounded-full bg-shariti-black hover:bg-slate-800 text-shariti-yellow transition ${className}`}
            >
                {loading
                    ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    : (playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4 fill-current" />)}
            </button>
            {/* Hidden native audio — controls are the button above */}
            {audioSrc && (
                <audio
                    ref={audioRef}
                    src={audioSrc}
                    preload="metadata"
                    onEnded={() => setPlaying(false)}
                    onPause={() => setPlaying(false)}
                    onPlay={() => setPlaying(true)}
                    className="hidden"
                    data-testid={`${dataTestid}-audio`}
                />
            )}
        </>
    );
}
